/**
 * Presentation rules for the unified Data surface (W4-UI-01).
 *
 * Everything here reads contract values and turns them into what the frames
 * DATA-R1-01..03 draw. Nothing invents a value: a fact the contract does not
 * carry is rendered as "not available yet" by the caller, never guessed.
 */
import type { components } from "@scalty/contracts";
import { CUSTOMER_TERMS } from "./terms";

export type DataObject = components["schemas"]["DataObject"];
export type DataObjectStateCounts = components["schemas"]["DataObjectStateCounts"];
type DataObjectList = components["schemas"]["DataObjectList"];
export type DataObjectRevision = components["schemas"]["DataObjectRevision"];

/**
 * Filtered-cohort lifecycle totals from the first cursor page. Later pages
 * omit counts, so callers receive null instead of reconstructing a total from
 * whichever rows happen to be loaded in the browser.
 */
export function dataObjectStateCounts(
  value: DataObjectList | undefined,
): DataObjectStateCounts | null {
  return value?.counts ?? null;
}

/** Markdown is the format the current reader understands. */
export const MARKDOWN_CONTENT_TYPE = "text/markdown";
/**
 * `sizeBytes.maximum` in the contract: what ONE upload request can carry. The
 * browser sends a file in a single signed request, and the storage protocol caps
 * one such request at 5 GiB. It is a bound of the transfer, not of ours on how
 * much data may be brought: nothing smaller is ever refused, and a file above it
 * is refused here, before a byte moves.
 */
export const MAX_SOURCE_BYTES = 5368709120;

/**
 * Display revision number. The API's first revision is `revisionNumber` 0; the
 * surface shows one-based `rN`, so the two never appear interchangeable.
 */
export function revisionLabel(revisionNumber: number): string {
  return `r${revisionNumber + 1}`;
}

/**
 * Abbreviated digest that keeps its algorithm. Dropping the `sha256:` prefix
 * would make the abbreviation something other than the value the product
 * carries, so the prefix stays and only the hex is shortened.
 */
export function shortDigest(digest: string | null | undefined): string | null {
  if (!digest) return null;
  const separator = digest.indexOf(":");
  if (separator === -1) return `${digest.slice(0, 4)}…`;
  return `${digest.slice(0, separator + 1)}${digest.slice(separator + 1, separator + 5)}…`;
}

/** Abbreviated 32-hex identifier. */
export function shortId(id: string | null | undefined): string | null {
  if (!id) return null;
  return id.length <= 14 ? id : `${id.slice(0, 14)}…`;
}

export function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const index = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const value = bytes / Math.pow(1024, index);
  return `${index === 0 ? value : Number(value.toFixed(1))} ${units[index]}`;
}

/**
 * The facts one data object's state is derived from. A document becomes memory
 * in one ingest job after it is put into a collection, so the object itself has
 * exactly one lifecycle: its upload. It is ready once any upload has finished.
 */
export interface DataObjectFacts {
  /** The newest revision, whatever its upload status. */
  revision: DataObjectRevision | null;
  /** The newest finished revision; null until an upload finishes. */
  currentRevisionId: string | null;
}

export type LifecycleFamily = "ready" | "in_progress" | "needs_attention";

export type DataListFilter = "all" | "ready" | "checking" | "refused";

/** Keep the API's neutral count fields mapped to the words the rows wear. */
export function dataObjectFilterOptions(counts: DataObjectStateCounts) {
  return [
    { value: "all" as const, label: "All", count: counts.total },
    { value: "ready" as const, label: "Ready", count: counts.ready },
    { value: "checking" as const, label: "Uploading", count: counts.checking },
    { value: "refused" as const, label: "Expired", count: counts.refused },
  ];
}

export interface DataObjectStatus {
  label: string;
  /** Badge variant from the shared design system. */
  variant: "ready" | "processing" | "running" | "error" | "secondary" | "archived";
  /** Dot/spinner treatment the status pills use in the frames. */
  indicator: "dot" | "spinner" | "none";
  family: LifecycleFamily;
  /** Row tint the frames use for refused rows. */
  tint: string;
}

/**
 * The status of one object, in the server's three states: ready once an upload
 * has finished (a later upload in flight does not take that away), checking
 * while the first upload is still arriving, refused when no upload finished and
 * the newest one expired.
 */
export function deriveStatus(facts: DataObjectFacts): DataObjectStatus | null {
  const { revision, currentRevisionId } = facts;
  if (currentRevisionId !== null || revision?.status === "finalized") {
    return {
      label: CUSTOMER_TERMS.states.ready,
      variant: "ready",
      indicator: "dot",
      family: "ready",
      tint: "",
    };
  }
  if (!revision) return null;
  switch (revision.status) {
    case "uploading":
      return {
        label: "Uploading",
        variant: "running",
        indicator: "spinner",
        family: "in_progress",
        tint: "",
      };
    case "finalizing":
      return {
        label: "Finalizing",
        variant: "running",
        indicator: "spinner",
        family: "in_progress",
        tint: "",
      };
    case "expired":
      return {
        label: "Expired",
        variant: "error",
        indicator: "none",
        family: "needs_attention",
        tint: "bg-destructive/[0.03]",
      };
  }
}

/** A terminal Data-row problem comes only from the server facts for that row. */
export function dataObjectTerminalProblem(facts: DataObjectFacts): string | null {
  if (facts.currentRevisionId === null && facts.revision?.status === "expired") {
    return "The upload expired before the file was received.";
  }
  return null;
}

export interface SurfaceFailure {
  /** One sentence naming what happened. */
  message: string;
  /** True only when repeating the same request could succeed unchanged. */
  retryable: boolean;
  /** Set when the customer must sign in again rather than retry. */
  signIn?: boolean;
}

/**
 * The list can fail in four distinct ways and each needs a different thing from
 * the customer, so one "try again" is wrong for three of them. Anything else —
 * a dropped connection, an unexpected status — is reported as the transient
 * failure it is.
 */
export function describeFailure(error: unknown, subject: string): SurfaceFailure {
  if (error instanceof ApiError) {
    switch (error.status) {
      case 400:
        return { message: `The request for ${subject} was not valid.`, retryable: false };
      case 401:
        return { message: "Your session has ended. Sign in again.", retryable: false, signIn: true };
      case 403:
        return { message: `You are not allowed to read ${subject}.`, retryable: false };
      case 404:
        return { message: `${subject} is not available in this project.`, retryable: false };
      case 501:
        return {
          message: "Data objects are not enabled in this deployment.",
          retryable: true,
        };
    }
    return { message: error.message, retryable: true };
  }
  return { message: `Couldn't load ${subject}. Nothing was changed.`, retryable: true };
}

/**
 * The list operation accepts a project, a limit and a cursor and nothing else,
 * so a name filter and lifecycle counts describe the rows currently loaded.
 * They are labelled that way on the surface rather than presented as server
 * facts about the whole project.
 */
export function matchesNameFilter(displayName: string, filter: string): boolean {
  const query = filter.trim().toLowerCase();
  return query === "" || displayName.toLowerCase().includes(query);
}
